import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  BellIcon,
} from '@heroicons/react/24/outline';

export default function Parametres() {
  const [activeTab, setActiveTab] = useState('communications');
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preferences`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPreferences(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des préférences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, value) => {
    setPreferences({
      ...preferences,
      communications: {
        ...preferences.communications,
        [category]: value
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/preferences`,
        { communications: preferences.communications },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessage({ type: 'success', text: 'Préférences enregistrées avec succès' });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  const communicationOptions = [
    {
      key: 'mesGroupements',
      label: 'Communications de mes groupements',
      description: `Recevoir les communications de ${user?.organisme || 'votre groupement'}`,
    },
    {
      key: 'autresGroupements',
      label: 'Communications des autres groupements',
      description: 'Recevoir les communications des groupements dont vous ne faites pas partie',
    },
    {
      key: 'alertesSanitaires',
      label: 'Intérêt général',
      description: 'Recevoir les informations d\'intérêt général (recommandé)',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
          <p className="mt-2 text-sm text-gray-600">
            Gérez vos préférences et paramètres de compte
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('communications')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'communications'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BellIcon className="h-5 w-5 inline-block mr-2" />
                Communications
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'communications' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-2">
                    Préférences de communication
                  </h2>
                  <p className="text-sm text-gray-600">
                    Choisissez les types de communications que vous souhaitez recevoir
                  </p>
                </div>

                {/* Message de confirmation */}
                {message && (
                  <div
                    className={`mb-6 p-4 rounded-md ${
                      message.type === 'success'
                        ? 'bg-green-50 text-green-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                {/* Options de communication */}
                <div className="space-y-4">
                  {communicationOptions.map((option) => (
                    <div
                      key={option.key}
                      className="p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="mb-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          {option.label}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {option.description}
                        </p>
                      </div>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={option.key}
                            checked={preferences?.communications[option.key] === true}
                            onChange={() => handleToggle(option.key, true)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Oui</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={option.key}
                            checked={preferences?.communications[option.key] === false}
                            onChange={() => handleToggle(option.key, false)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Non</span>
                        </label>
                      </div>
                      {
                        option.key === 'alertesSanitaires' && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Note :</strong> Les informations d'intérêt général sont fortement recommandées
                              pour rester informé des problématiques importantes concernant vos ruches et notamment les alertes sanitaires.
                            </p>
                          </div>
                        )
                      }
                    </div>
                  ))}
                </div>

                {/* Bouton de sauvegarde */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer les préférences'}
                  </button>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
